import type { ReactNode } from 'react';
import { MdArrowBack } from 'react-icons/md';

import { cn } from '../lib/cn';

export type PageHeaderProps = {
  title: string;
  description?: ReactNode;
  /** Icône discrète, alignée sur la ligne du titre. */
  icon?: ReactNode;
  /**
   * Remontée vers la page parente, rendue au-dessus du titre. Volontairement
   * hors du groupe `actions` : revenir en arrière n'est pas une action sur la
   * page, et la mêler aux boutons de droite la noie parmi eux.
   */
  back?: { label: string; onClick: () => void };
  /** Boutons alignés à droite. */
  actions?: ReactNode;
  className?: string;
};

/** En-tête de page : retour, titre (précédé d'une icône), description, actions. */
export function PageHeader({
  title,
  description,
  icon,
  back,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {back ? (
        <button
          type="button"
          onClick={back.onClick}
          className="-ml-1 inline-flex w-fit items-center gap-1 rounded-lg px-1 py-0.5 text-sm font-medium text-content-muted transition-colors hover:text-content"
        >
          <MdArrowBack className="size-4" aria-hidden="true" />
          {back.label}
        </button>
      ) : null}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-2">
            {icon ? (
              <span aria-hidden="true" className="flex shrink-0 text-content-muted [&>svg]:size-5">
                {icon}
              </span>
            ) : null}
            <h1 className="truncate text-xl font-semibold text-content">{title}</h1>
          </div>
          {description ? (
            <p className="text-sm font-medium text-content/70">{description}</p>
          ) : null}
        </div>

        {actions ? <div className="flex shrink-0 items-center gap-3">{actions}</div> : null}
      </div>
    </div>
  );
}
